import { SCHEMA } from '@vedalib/editor/lib/brand'
import { useCallback, useMemo } from 'react'
import React from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import { FieldColorPicker, FieldSegmented, FieldSlider, FormField } from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { MediaFormBrandType } from '../mediaTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.video.style, {
  player: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  play: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.video.tags.play'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  icons: {
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.iconsColor'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  activeCC: {
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.video.schema.activeCC'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  slider: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.video.schema.sliderBackground'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  sliderFilled: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.video.schema.sliderFilled'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  border: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.video.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const VideoFormStyle: MediaFormBrandType = ({ device, subConfigs, edited, setForm }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom bottomSpace gap sides>
        <Typography weight='medium'>{t('elements.audio.tags.player')}</Typography>
        <FormField config={schema.player.backgroundColor} />
        <FormField config={schema.play.backgroundColor} />
        <FormField config={schema.hover.backgroundColor} />
        <FormField config={schema.icons.color} />
        <FormField config={schema.activeCC.color} />
        <FormField config={schema.slider.backgroundColor} />
        <FormField config={schema.sliderFilled.backgroundColor} />

        <Typography weight='medium'>{t('elements.audio.tags.border')}</Typography>
        <FormField config={schema.border.borderRadius} />
        <FormField config={schema.border.borderStyle} />
        <FormField config={schema.border.borderColor} />
        <FormField config={schema.border.borderWidth} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(VideoFormStyle) as typeof VideoFormStyle
